import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Circle, Sparkles } from 'lucide-react';
import { ApiError, api, type Me } from '../lib/api.js';
import { hasCapability } from '../lib/access.js';
import { ago } from '../lib/format.js';
import { Panel, cn, useToast } from '../ui/index.js';
import { FoldTitle, usePanelFold } from './PanelFold.js';

interface Ask {
  ask: string;
  status: 'open' | 'answered' | 'done';
  article: string | null;
  doneBy: string | null;
  doneAt: string | null;
}

/**
 * What the request asked for, one item at a time.
 *
 * Only on a ticket whose email asked for more than one thing. What the
 * assistant already answered says so, with the article; the rest is ticked
 * off here as the desk does it -- so nobody answers the leave question twice
 * and nobody forgets the flowers. Open by default: it is the to-do list.
 */
export function TicketAsks({ tenant, number, me }: { tenant: string; number: number; me: Me | undefined }) {
  const qc = useQueryClient();
  const toast = useToast();
  const key = ['ticket-asks', tenant, number];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.get<{ asks: Ask[] }>(`/api/v1/tenants/${tenant}/tickets/${number}/asks`),
    enabled: hasCapability(me, tenant, 'ticket.read.queue'),
  });
  const mayTick = hasCapability(me, tenant, 'ticket.field.write');
  const [open, toggle] = usePanelFold('asks', true);
  const mark = useMutation({
    mutationFn: ({ index, done }: { index: number; done: boolean }) =>
      api.patch<{ asks: Ask[] }>(`/api/v1/tenants/${tenant}/tickets/${number}/asks/${index}`, { done }),
    onSuccess: (r) => qc.setQueryData(key, r),
    onError: (e) => toast.error('Not changed', e instanceof ApiError ? e.message : undefined),
  });

  const asks = q.data?.asks ?? [];
  if (asks.length === 0) return null;
  const left = asks.filter((a) => a.status === 'open').length;

  const summary = (
    <span className={left > 0 ? 'text-warn' : 'text-ok'}>
      {left > 0 ? `${left} of ${asks.length} still to do` : `all ${asks.length} done`}
    </span>
  );

  return (
    <Panel title={<FoldTitle open={open} onToggle={toggle} title="What they asked for" summary={summary} />}>
      {open && (
        <ul className="space-y-1.5">
          {asks.map((a, i) => {
            const answered = a.status === 'answered';
            const done = a.status === 'done';
            const tickable = mayTick && !answered;
            return (
              <li key={i} className="flex items-start gap-2 text-xs">
                <button
                  type="button"
                  disabled={!tickable || mark.isPending}
                  onClick={() => mark.mutate({ index: i, done: !done })}
                  aria-label={done ? `Mark "${a.ask}" as still to do` : `Mark "${a.ask}" as done`}
                  aria-pressed={done}
                  className={cn(
                    'mt-px shrink-0 rounded-full',
                    tickable ? 'hover:text-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand' : 'cursor-default',
                  )}
                >
                  {answered ? (
                    <Sparkles aria-hidden className="h-3.5 w-3.5 text-brand" />
                  ) : done ? (
                    <CheckCircle2 aria-hidden className="h-3.5 w-3.5 text-ok" />
                  ) : (
                    <Circle aria-hidden className="h-3.5 w-3.5 text-ink-tertiary" />
                  )}
                </button>
                <div className="min-w-0">
                  <p className={cn('text-ink', done && 'text-ink-secondary line-through')}>{a.ask}</p>
                  {answered && (
                    <p className="text-2xs text-ink-tertiary">
                      Answered by the assistant{a.article ? ` from "${a.article}"` : ''}
                    </p>
                  )}
                  {done && a.doneBy && (
                    <p className="text-2xs text-ink-tertiary">
                      Done by {a.doneBy}
                      {a.doneAt ? ` ${ago(a.doneAt)}` : ''}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
